<div class="flex flex-column w-full" role="region" [attr.aria-label]="title">
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true"></p-toast>

  @if (data.length > 0 || hasAnyActiveFilters()) {
    <section
      class="surface-0 border-round p-5 mb-4 shadow-1"
      [attr.aria-labelledby]="title + '-heading'"
    >
      <!-- Table Header -->
      <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
        <i [class]="'pi ' + icon + ' text-2xl section-icon'" aria-hidden="true"></i>
        <h2 [id]="title + '-heading'" class="text-xl font-semibold m-0 section-title">
          {{ title }}
        </h2>
      </div>

      <!-- Actions -->
      <div class="flex justify-content-end align-items-center gap-2 mb-3">
        @if (showClearFiltersButton) {
          <p-button
            label="Azzera filtri"
            icon="pi pi-filter-slash"
            [outlined]="true"
            (onClick)="clearAllFilters(dt)"
            [ariaLabel]="'Azzera tutti i filtri della tabella ' + title"
            [attr.title]="'Azzera tutti i filtri della tabella ' + title"
            [pTooltip]="'Azzera tutti i filtri della tabella ' + title"
            tooltipPosition="top"
          ></p-button>
        }

        @if (showExportButton) {
          <p-button
            label="Esporta"
            icon="pi pi-download"
            [outlined]="true"
            (onClick)="handleExport()"
            [ariaLabel]="'Esporta i dati della tabella ' + title"
            [attr.title]="'Esporta i dati della tabella ' + title"
            [pTooltip]="'Esporta i dati della tabella ' + title"
            tooltipPosition="top"
          ></p-button>
        }

        <!-- Optional projected toolbar content (e.g. export, filter toggles) -->
        <ng-content select="[toolbar-extra]"></ng-content>
      </div>

      <!-- TABLE -->
      <p-table
        #dt
        [value]="data"
        [tableStyle]="{ 'min-width': '50rem' }"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50, 100]"
        [showCurrentPageReport]="true"
        [currentPageReportTemplate]="'Mostrando {first} - {last} di {totalRecords} elementi'"
        [loading]="loading"
        [attr.aria-busy]="loading"
        [attr.aria-label]="title + ' - tabella dati con ' + data.length + ' elementi'"
      >
        <ng-template pTemplate="caption">
          <span class="sr-only"
            >{{ title }}: tabella con filtri e ordinamento. Usa le frecce per ordinare le
            colonne.</span
          >
        </ng-template>
        <ng-template pTemplate="header">
          <tr>
            @for (col of columns; track col.title) {
              <th
                [pSortableColumn]="col.field || ''"
                [style]="{ 'background-color': '#F0F4FF' }"
                [ngClass]="{ 'no-sort': !col.field }"
                scope="col"
                [attr.aria-sort]="
                  col.field && dt.isSorted(col.field)
                    ? dt.sortOrder === 1
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                "
              >
                <div class="flex flex-column gap-2">
                  <div class="flex align-items-center gap-2 white-space-nowrap">
                    <span class="font-semibold">{{ col.title }}</span>
                    @if (col.field) {
                      <p-sortIcon
                        [field]="col.field"
                        [attr.role]="'img'"
                        [attr.aria-label]="'Ordinamento colonna ' + col.title"
                        [attr.title]="'Ordinamento colonna ' + col.title"
                      ></p-sortIcon>
                      <span class="sr-only">Icona ordinamento per la colonna {{ col.title }}</span>
                    }
                  </div>
                  @if (col.filterType === 'text' && col.field) {
                    <input
                      pInputText
                      type="text"
                      class="table-filter-input"
                      [disabled]="isFilterLocked(col.field)"
                      [ngModel]="filterValues()[col.field]"
                      (ngModelChange)="setFilterValue(col.field, $event)"
                      (input)="onTextFilterInput($any($event.target).value, col.field, dt)"
                      (click)="$event.stopPropagation()"
                      (keydown.space)="$event.stopPropagation()"
                      [placeholder]="col.filterPlaceholder || 'Cerca...'"
                      [attr.aria-label]="'Filtra per ' + col.title"
                      [attr.aria-invalid]="hasFilterError(col.field)"
                      [attr.aria-describedby]="
                        hasFilterError(col.field) ? 'filter-error-' + col.field : null
                      "
                      [class.filter-input-invalid]="hasFilterError(col.field)"
                      [ngStyle]="{ width: '150px', 'max-width': '150px' }"
                    />
                    @if (getFilterError(col.field); as filterError) {
                      <div
                        class="filter-feedback"
                        [id]="'filter-error-' + col.field"
                        role="alert"
                        aria-live="polite"
                      >
                        <div class="filter-error">{{ filterError.message }}</div>
                        <div class="filter-suggestion">{{ filterError.suggestion }}</div>
                      </div>
                    }
                  } @else if (col.filterType === 'select' && col.field) {
                    <div (click)="$event.stopPropagation()">
                      <p-select
                        [options]="col.filterOptions || []"
                        [disabled]="isFilterLocked(col.field)"
                        [ngModel]="filterValues()[col.field]"
                        (ngModelChange)="setFilterValue(col.field, $event)"
                        (onChange)="applyFilter($event.value, col.field, 'equals', dt)"
                        (onClear)="applyFilter(null, col.field, 'equals', dt)"
                        [placeholder]="col.filterPlaceholder || 'Filtra...'"
                        [attr.aria-label]="'Filtra per ' + col.title"
                        [showClear]="!isFilterLocked(col.field)"
                        appendTo="body"
                        [ngStyle]="{ width: '150px', 'max-width': '150px' }"
                      >
                      </p-select>
                    </div>
                  } @else if (col.filterType === 'multiselect' && col.field) {
                    <div (click)="$event.stopPropagation()">
                      <p-multiselect
                        [options]="col.filterOptions || []"
                        [disabled]="isFilterLocked(col.field)"
                        [ngModel]="filterValues()[col.field]"
                        (ngModelChange)="setFilterValue(col.field, $event)"
                        (onChange)="onMultiSelectFilter($event.value, col.field, dt)"
                        (onClear)="onMultiSelectFilter([], col.field, dt)"
                        [placeholder]="col.filterPlaceholder || 'Filtra...'"
                        [attr.aria-label]="'Filtra per ' + col.title"
                        [showClear]="!isFilterLocked(col.field)"
                        appendTo="body"
                        [ngStyle]="{ width: '170px', 'max-width': '170px' }"
                        selectedItemsLabel="{0} selezionati"
                        [maxSelectedLabels]="1"
                      >
                      </p-multiselect>
                    </div>
                  } @else if (col.filterType === 'date' && col.field) {
                    <div (click)="$event.stopPropagation()" class="date-filter-wrapper">
                      <p-datepicker
                        #datepicker
                        styleClass="table-filter-datepicker"
                        [disabled]="isFilterLocked(col.field)"
                        [ngModel]="filterValues()[col.field]"
                        (ngModelChange)="setFilterValue(col.field, $event); onDateTextInput($any($event), col.field, dt)"
                        (onSelect)="onDateFilter($event, col.field, dt)"
                        (onClear)="applyFilter('', col.field, 'contains', dt)"
                        (onInput)="onDateManualInput($event, col.field)"
                        [placeholder]="col.filterPlaceholder || 'Filtra data'"
                        [attr.aria-label]="'Filtra per ' + col.title"
                        [attr.aria-invalid]="hasFilterError(col.field)"
                        [attr.aria-describedby]="
                          hasFilterError(col.field) ? 'filter-error-' + col.field : null
                        "
                        [inputStyleClass]="
                          hasFilterError(col.field)
                            ? 'table-filter-input filter-input-invalid'
                            : 'table-filter-input'
                        "
                        [showClear]="!isFilterLocked(col.field)"
                        dateFormat="dd/mm/yy"
                        appendTo="body"
                        [inputStyle]="{ width: '110px', 'max-width': '110px' }"
                      >
                      </p-datepicker>
                      @if (getFilterError(col.field); as filterError) {
                        <div
                          class="filter-feedback"
                          [id]="'filter-error-' + col.field"
                          role="alert"
                          aria-live="polite"
                        >
                          <div class="filter-error">{{ filterError.message }}</div>
                          <div class="filter-suggestion">{{ filterError.suggestion }}</div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </th>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-rowData>
          <tr [ngClass]="rowStyleClass ? rowStyleClass(rowData) : ''">
            @for (col of columns; track col.title) {
              <td>
                @if (col.type === 'tag' && col.field) {
                  @if (rowData[col.field]) {
                    <p-tag
                      [value]="getTagDisplay(rowData[col.field], col.field)"
                      [severity]="getTagSeverity(rowData[col.field], col.field)"
                      [attr.role]="'status'"
                      [attr.aria-label]="
                        col.title + ': ' + getTagDisplay(rowData[col.field], col.field)
                      "
                    >
                    </p-tag>
                  } @else {
                    <span>—</span>
                  }
                } @else if (col.type === 'action') {
                  <div class="flex gap-2">
                    @if (customActionsTemplate) {
                      <ng-container *ngTemplateOutlet="customActionsTemplate; context: { $implicit: rowData }"></ng-container>
                    } @else {
                    <!-- Seleziona - per selezione fornitore o altro -->
                    @if (onSelectClick.observed) {
                      <p-button
                        label="Seleziona"
                        icon="pi pi-check"
                        [outlined]="true"
                        severity="primary"
                        (onClick)="handleSelectClick(rowData)"
                        [attr.aria-label]="
                          'Seleziona ' + (rowData.ragioneSociale || rowData.name || rowData.id)
                        "
                      ></p-button>
                    }

                    <!-- Preview/Dettagli - mostrato se c'è listener e (ignoreStatusChecks è true O status NON è DRAFT O status è GENERATED) -->
                    @if (
                      onDetailClick.observed &&
                      (ignoreStatusChecks ||
                        rowData.status === 'GENERATED' ||
                        (rowData.status && rowData.status !== 'DRAFT'))
                    ) {
                      <p-button
                        icon="pi pi-eye"
                        [text]="true"
                        [rounded]="true"
                        severity="info"
                        styleClass="detail-view-btn"
                        (onClick)="handleDetailClick(rowData)"
                        [attr.aria-label]="
                          'Visualizza dettagli di ' +
                          (rowData.name ||
                            rowData.contractId ||
                            rowData.supplierName ||
                            rowData.businessName ||
                            rowData.id)
                        "
                        pTooltip="Visualizza dettagli"
                        tooltipPosition="top"
                      ></p-button>
                    }

                    <!-- Duplica - mostrato se c'è listener per duplica -->
                    @if (onDuplicateClick.observed) {
                      <p-button
                        icon="pi pi-copy"
                        [text]="true"
                        [rounded]="true"
                        severity="help"
                        (onClick)="handleDuplicateClick(rowData)"
                        [attr.aria-label]="
                          'Duplica ' +
                          (rowData.name ||
                            rowData.contractId ||
                            rowData.supplierName ||
                            rowData.businessName ||
                            rowData.id)
                        "
                        pTooltip="Duplica"
                        tooltipPosition="top"
                      ></p-button>
                    }

                    <!-- Modifica - mostrato se c'è un listener e (ignoreStatusChecks è true O status è DRAFT ma NON GENERATED) -->
                    @if (canEditRow(rowData)) {
                      <p-button
                        icon="pi pi-pencil"
                        [text]="true"
                        [rounded]="true"
                        severity="secondary"
                        (onClick)="handleEditClick(rowData)"
                        [attr.aria-label]="
                          'Modifica ' +
                          (rowData.contractId ||
                            rowData.supplierName ||
                            rowData.businessName ||
                            rowData.id)
                        "
                        pTooltip="Modifica"
                        tooltipPosition="top"
                      ></p-button>
                    }

                    <!-- Elimina - mostrato se c'è un listener e:
                         - lo status non è in deleteExcludedStatuses (se definita), E
                         - (ignoreStatusChecks è true O status è DRAFT/INACTIVE/GENERATED) -->
                    @if (canDeleteRow(rowData)) {
                      <p-button
                        icon="pi pi-trash"
                        [text]="true"
                        [rounded]="true"
                        severity="danger"
                        (onClick)="handleDeleteClick(rowData)"
                        [attr.aria-label]="
                          'Elimina ' +
                          (rowData.contractId ||
                            rowData.supplierName ||
                            rowData.businessName ||
                            rowData.id)
                        "
                        pTooltip="Elimina"
                        tooltipPosition="top"
                      ></p-button>
                    }

                    <!-- Download - mostrato solo se c'è un listener -->
                    @if (onDownloadClick.observed) {
                      <p-button
                        icon="pi pi-download"
                        [text]="true"
                        [rounded]="true"
                        severity="success"
                        (onClick)="handleDownloadClick(rowData)"
                        [attr.aria-label]="
                          'Scarica documentale di ' +
                          (rowData.contractId ||
                            rowData.supplierName ||
                            rowData.businessName ||
                            rowData.id)
                        "
                        pTooltip="Scarica documentale"
                        tooltipPosition="top"
                      ></p-button>
                    }
                    }
                  </div>
                } @else if (col.type === 'truncated' && col.field) {
                  <span
                    [pTooltip]="rowData[col.field + '_full'] ?? rowData[col.field]"
                    tooltipPosition="top"
                    [tooltipDisabled]="!(rowData[col.field + '_full'])"
                    style="cursor: default"
                  >{{ rowData[col.field] }}</span>
                } @else if (col.type === 'date' && col.field) {
                  {{ formatDateTime(rowData[col.field]) }}
                } @else if (col.field) {
                  {{ rowData[col.field] }}
                }
              </td>
            }
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="columns.length" class="text-center py-6 text-color-secondary">
              @if (hasAnyActiveFilters()) {
                Nessun elemento corrisponde ai filtri applicati. Rimuovi o modifica i filtri per visualizzare risultati.
              } @else {
                {{ emptyStateMessage }}
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  } @else {
    <!-- EMPTY STATE -->
    <section class="surface-0 border-round p-5 mb-4 shadow-1">
      <div
        class="flex flex-column align-items-center justify-content-center p-8 gap-3"
        style="min-height: 400px"
      >
        <i
          [class]="'pi ' + (hasActiveFilters() ? 'pi-filter-slash' : 'pi-inbox')"
          style="font-size: 4rem; color: var(--text-color-secondary)"
          aria-hidden="true"
        ></i>
        @if (hasAnyActiveFilters()) {
          <h3 class="m-0 text-xl font-semibold">Nessun risultato trovato</h3>
          <p class="m-0 text-color-secondary">Nessun elemento corrisponde ai filtri applicati. Prova a modificare o rimuovere alcuni filtri.</p>
        } @else {
          <h3 class="m-0 text-xl font-semibold">{{ emptyStateTitle }}</h3>
          <p class="m-0 text-color-secondary">{{ emptyStateMessage }}</p>
        }
      </div>
    </section>
  }
</div>
